import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import PageContainer from '../PageContainer';
import { LIST_ROWS_PER_PAGE, VISIBLE_PAGE_COUNT } from '../../constants';
import { useEffect, useMemo, useState } from 'react';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useVettingApplicationsQuery } from '../../api/use-vetting-applications-query';
import { useVettingConfigQuery } from '../../api/use-vetting-config-query';
import { formatVettingDateTime } from '../../services/vettingApplications';
import { getUserInitial } from '../../utils/userDisplay';
import {
  getVettingDecisionMeta,
  getVettingScoreMeta,
  getVettingThresholds,
  VettingStatusChip,
} from './vettingDecisionDisplay';
import { CancelRounded, CheckCircle } from '@mui/icons-material';
import CommunityMemberProfileDialog from '../Partner/Community/widgets/CommunityMemberProfileDialog';
import { mapCommunityMemberProfile } from '../Partner/Community/mapCommunityMemberProfile';
import PortalPaginationControls from '../layout/PortalPaginationControls';
import {
  PortalTableCell,
  PortalTableHead,
  PortalTableRow,
  PORTAL_TABLE_NAME_HOVER,
} from '../../styles/portalListTable';
import { PARTNER_SWITCH_SX } from '../Partner/partnerDashboardTheme';

/** Listing filter values — sent as `type` query param (omitted when `all`). */
export const VETTING_LIST_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'waitlisted', label: 'Waitlisted' },
];

const SEARCH_DEBOUNCE_MS = 400;

const searchFieldWrapSx = {
  flex: '0 0 auto',
  position: 'relative',
  width: 240,
};

const searchFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: 'background.paper',
    fontSize: '0.875rem',
    pr: 4.5,
  },
};

const searchSpinnerSx = {
  position: 'absolute',
  right: 12,
  top: '50%',
  mt: '-8px',
  pointerEvents: 'none',
};

const ActionIconButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
}));

const statusFilterGroupSx = {
  flexWrap: 'wrap',
  gap: 0.75,
  '& .MuiToggleButtonGroup-grouped': {
    border: '1px solid',
    borderColor: 'divider !important',
    borderRadius: '20px !important',
    margin: 0,
    px: 1.75,
    py: 0.5,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: 'text.secondary',
    bgcolor: 'background.paper',
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      borderColor: 'primary.main !important',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
};

const canShowSubscribedOnlyFilter = (statusFilter) =>
  statusFilter === 'all' || statusFilter === 'accepted';

const VettedCommunityUsers = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [subscribedOnly, setSubscribedOnly] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const showSubscribedOnlyFilter = canShowSubscribedOnlyFilter(statusFilter);
  const limit = LIST_ROWS_PER_PAGE;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const queryParams = useMemo(() => {
    const params = { page, limit };
    if (statusFilter && statusFilter !== 'all') {
      params.type = statusFilter;
    }
    if (showSubscribedOnlyFilter && subscribedOnly) {
      params.subscribedOnly = true;
    }
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    return params;
  }, [limit, page, statusFilter, showSubscribedOnlyFilter, subscribedOnly, debouncedSearch]);

  const { data, isLoading, isFetching, error } = useVettingApplicationsQuery(queryParams);
  const isRefetching = isFetching && !isLoading;
  const { data: configData } = useVettingConfigQuery();

  const thresholds = useMemo(() => getVettingThresholds(configData?.active), [configData?.active]);

  const users = data?.applications ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, totalPages);

  const openUserProfile = (application) => {
    setSelectedMember(mapCommunityMemberProfile(application));
  };

  const handleStatusFilterChange = (_event, nextValue) => {
    if (nextValue == null) return;
    setStatusFilter(nextValue);
    if (!canShowSubscribedOnlyFilter(nextValue)) {
      setSubscribedOnly(false);
    }
    setPage(1);
  };

  const handleSubscribedOnlyChange = (_event, checked) => {
    setSubscribedOnly(checked);
    setPage(1);
  };

  return (
    <PageContainer title="Vetted Users">
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <Box sx={searchFieldWrapSx}>
            <TextField
              size="small"
              placeholder="Search users"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={searchFieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            {isRefetching ? (
              <CircularProgress size={16} color="inherit" sx={searchSpinnerSx} />
            ) : null}
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            aria-label="Filter by decision status"
            sx={statusFilterGroupSx}
          >
            {VETTING_LIST_STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {showSubscribedOnlyFilter ? (
            <FormControlLabel
              control={
                <Switch
                  checked={subscribedOnly}
                  onChange={handleSubscribedOnlyChange}
                  size="small"
                  sx={PARTNER_SWITCH_SX}
                />
              }
              label="Subscribed Only"
              sx={{
                ml: 0,
                mr: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: 'text.secondary',
                },
              }}
            />
          ) : null}
        </Box>

        <Button
          variant="contained"
          color="primary"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/vetted-users/config')}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '20px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          Manage Config
        </Button>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="primary" />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      )}

      {!isLoading && !error && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 'calc(100vh - 340px)',
                minHeight: 0,
                overflow: 'auto',
                opacity: isRefetching ? 0.6 : 1,
                transition: 'opacity 0.15s ease',
              }}
            >
              <Table stickyHeader>
                <PortalTableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Submitted On</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>Subscription</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Invited</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </PortalTableHead>
                <TableBody>
                  {users.length === 0 ? (
                    <PortalTableRow>
                      <PortalTableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No users found.
                        </Typography>
                      </PortalTableCell>
                    </PortalTableRow>
                  ) : (
                    users.map((user) => (
                      <PortalTableRow key={user.id || user.email}>
                        <PortalTableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              cursor: 'pointer',
                              minWidth: 0,
                              maxWidth: '100%',
                              '&:hover .user-name': { color: PORTAL_TABLE_NAME_HOVER },
                            }}
                            onClick={() => openUserProfile(user)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openUserProfile(user);
                              }
                            }}
                          >
                            <Avatar src={user.avatarUrl || undefined} alt={user.fullName}>
                              {getUserInitial(user.fullName, user.userEmail)}
                            </Avatar>
                            <Typography
                              className="user-name"
                              variant="body2"
                              sx={{
                                minWidth: 0,
                                fontWeight: 500,
                                transition: 'color 0.15s ease',
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.fullName}
                            </Typography>
                          </Box>
                        </PortalTableCell>
                        <PortalTableCell>
                          <Typography variant="body2">
                            {formatVettingDateTime(user.submittedAt)}
                          </Typography>
                        </PortalTableCell>
                        <PortalTableCell>
                          <Typography variant="body2">{user.city || '-'}</Typography>
                        </PortalTableCell>
                        <PortalTableCell>
                          {user.vettedSubscriptionProductName ? (
                            <Chip
                              label={user.vettedSubscriptionProductName}
                              size="small"
                              sx={{
                                height: 24,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                borderRadius: '6px',
                                bgcolor: 'rgba(37, 99, 235, 0.12)',
                                color: '#1d4ed8',
                              }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </PortalTableCell>
                        <PortalTableCell>
                          <VettingStatusChip
                            label={user.totalScore ?? '-'}
                            config={getVettingScoreMeta(user.totalScore, thresholds)}
                          />
                        </PortalTableCell>
                        <PortalTableCell>
                          <VettingStatusChip
                            label={
                              user.decision ||
                              getVettingDecisionMeta(null, user.decision, thresholds).label
                            }
                            config={getVettingDecisionMeta(null, user.decision, thresholds)}
                          />
                        </PortalTableCell>
                        <PortalTableCell align="center">
                          {user.invited ? (
                            <CheckCircle
                              titleAccess="Used invite code"
                              sx={{ fontSize: 20, color: '#2e7d32' }}
                            />
                          ) : (
                            <CancelRounded
                              titleAccess="Did not use invite code"
                              sx={{ fontSize: 20, color: '#9e9e9e' }}
                            />
                          )}
                        </PortalTableCell>
                        <PortalTableCell>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <ActionIconButton
                              aria-label="View application"
                              onClick={() => navigate(`/vetted-users/${user.id}`)}
                            >
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            </ActionIconButton>
                          </Box>
                        </PortalTableCell>
                      </PortalTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderTop: '1px solid',
                  borderTopColor: 'divider',
                  bgcolor: 'background.paper',
                  flexShrink: 0,
                }}
              >
                <PortalPaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  visibleCount={VISIBLE_PAGE_COUNT}
                  isLoading={isFetching}
                  onPageChange={setPage}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      <CommunityMemberProfileDialog
        open={Boolean(selectedMember)}
        member={selectedMember}
        onClose={() => setSelectedMember(null)}
        showPhoneNumber
      />
    </PageContainer>
  );
};

export default VettedCommunityUsers;
